import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { User } from '../types'
import { authApi, setAuthTokens, clearAuthTokens, getAccessToken, getRefreshToken } from '../services/api'

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithLink: (token: string) => Promise<void>
  register: (data: { email: string; password: string; firstName: string; lastName: string; phone?: string }) => Promise<void>
  logout: () => Promise<void>
  updateProfile: (data: { firstName?: string; lastName?: string; phone?: string }) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshUser = useCallback(async () => {
    const token = getAccessToken()
    if (!token) {
      setLoading(false)
      return
    }

    try {
      const response = await authApi.getProfile()
      if (response.data.success && response.data.data) {
        setUser(response.data.data)
      } else {
        clearAuthTokens()
        setUser(null)
      }
    } catch (error) {
      clearAuthTokens()
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshUser()
  }, [refreshUser])

  const login = async (email: string, password: string) => {
    const response = await authApi.login({ email, password })
    if (response.data.success && response.data.data) {
      // بک‌اند refreshToken را در بدنه هم برمی‌گرداند (روتیشن‌شده)؛ اگر نیامد، قبلی حفظ می‌شود
      const { accessToken, refreshToken } = response.data.data
      setAuthTokens(accessToken, refreshToken ?? getRefreshToken() ?? '')
      setUser(response.data.data.user)
    } else {
      throw new Error(response.data.message || 'خطا در ورود')
    }
  }

  /** ورود با لینک اختصاصی که مدیر برای کاربر ساخته است */
  const loginWithLink = useCallback(async (token: string) => {
    const response = await authApi.loginWithLink(token)
    if (response.data.success && response.data.data) {
      const { accessToken, refreshToken } = response.data.data
      setAuthTokens(accessToken, refreshToken ?? getRefreshToken() ?? '')
      setUser(response.data.data.user)
    } else {
      throw new Error(response.data.message || 'لینک ورود نامعتبر است')
    }
  }, [])

  const register = async (data: { email: string; password: string; firstName: string; lastName: string; phone?: string }) => {
    const response = await authApi.register(data)
    if (response.data.success && response.data.data) {
      setAuthTokens(response.data.data.tokens.accessToken, response.data.data.tokens.refreshToken)
      setUser(response.data.data.user)
    } else {
      throw new Error(response.data.message || 'خطا در ثبت‌نام')
    }
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch {
      // Ignore logout errors
    } finally {
      clearAuthTokens()
      setUser(null)
    }
  }

  const updateProfile = async (data: { firstName?: string; lastName?: string; phone?: string }) => {
    const response = await authApi.updateProfile(data)
    if (response.data.success && response.data.data) {
      setUser(response.data.data)
    } else {
      throw new Error(response.data.message || 'خطا در بروزرسانی پروفایل')
    }
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const response = await authApi.changePassword({ currentPassword, newPassword })
    if (!response.data.success) {
      throw new Error(response.data.message || 'خطا در تغییر رمز عبور')
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithLink, register, logout, updateProfile, changePassword, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}